import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Zap, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }

    if (!/\d/.test(password)) {
      setError('La password deve contenere almeno 1 numero');
      return;
    }

    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Link scaduto o non valido. Richiedi un nuovo reset.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24">
        <div className="text-center">
          <h1 className="font-['Outfit'] text-2xl font-medium mb-4">Link non valido</h1>
          <p className="text-[#A1A1AA] mb-6">Il link di reset password non è valido o è scaduto.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8">
              Richiedi nuovo link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0055FF] flex items-center justify-center">
            <Zap className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-['Outfit'] font-semibold text-2xl tracking-tight">
            Orchestra <span className="text-[#0055FF]">AI</span>
          </span>
        </Link>

        {/* Card */}
        <div className="bg-[#111111] border border-white/10 rounded-3xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-medium mb-2">Password Aggiornata!</h1>
              <p className="text-[#A1A1AA] mb-6">
                La tua password è stata reimpostata con successo. Verrai reindirizzato al login...
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-['Outfit'] text-2xl font-medium text-center mb-2">Nuova Password</h1>
              <p className="text-[#A1A1AA] text-center mb-8">
                Crea una nuova password per il tuo account
              </p>

              {error && (
                <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="text-sm font-medium mb-2 block">Nuova Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimo 8 caratteri"
                      className="input-field pr-12"
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-sm font-medium mb-2 block">Conferma Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="input-field"
                    required
                    data-testid="reset-confirm-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full py-6 font-medium"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Salvataggio...' : 'Salva Nuova Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
